import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Plus, Edit2, Trash2, X, HelpCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminFAQs = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    order: 0
  });

  const fetchFAQs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/faqs`);
      setFaqs(response.data);
    } catch (error) {
      toast.error('Failed to fetch FAQs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFAQs();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const openCreateModal = () => {
    setEditing(null);
    setFormData({ question: '', answer: '', order: faqs.length + 1 });
    setShowModal(true);
  };

  const openEditModal = (faq) => {
    setEditing(faq);
    setFormData({
      question: faq.question,
      answer: faq.answer,
      order: faq.order || 0
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    const payload = {
      ...formData,
      order: parseInt(formData.order) || 0
    };

    try {
      if (editing) {
        await axios.put(`${API_URL}/api/faqs/${editing.id}`, payload, { headers });
        toast.success('FAQ updated');
      } else {
        await axios.post(`${API_URL}/api/faqs`, payload, { headers });
        toast.success('FAQ created');
      }
      setShowModal(false);
      fetchFAQs();
    } catch (error) {
      toast.error('Failed to save FAQ');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) return;
    
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API_URL}/api/faqs/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('FAQ deleted');
      fetchFAQs();
    } catch (error) {
      toast.error('Failed to delete FAQ');
    }
  };

  return (
    <div data-testid="admin-faqs">
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-['Oswald'] text-3xl text-[#F2EDE6]">FAQS</h1>
        <button
          onClick={openCreateModal}
          data-testid="add-faq-btn"
          className="btn-primary flex items-center gap-2 text-sm"
        >
          <Plus size={16} />
          Add FAQ
        </button>
      </div>

      {loading ? (
        <p className="text-[#A19D98]">Loading...</p>
      ) : faqs.length === 0 ? (
        <p className="text-[#A19D98]">No FAQs yet.</p>
      ) : (
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={faq.id}
              data-testid={`faq-row-${faq.id}`}
              className="bg-[#141414] border border-[#2A2A2A] p-6"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <HelpCircle className="w-5 h-5 text-[#E8651A]" />
                    <span className="text-[#A19D98] text-xs">#{index + 1}</span>
                  </div>
                  <h3 className="text-[#F2EDE6] font-['Oswald'] text-lg mb-2">{faq.question}</h3>
                  <p className="text-[#A19D98] text-sm line-clamp-2">{faq.answer}</p>
                </div>
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => openEditModal(faq)}
                    className="p-2 text-[#A19D98] hover:text-[#E8651A]"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(faq.id)}
                    className="p-2 text-[#A19D98] hover:text-[#FF3B30]"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6">
          <div className="bg-[#141414] border border-[#2A2A2A] p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-['Oswald'] text-2xl text-[#F2EDE6]">
                {editing ? 'EDIT FAQ' : 'ADD FAQ'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-[#A19D98] hover:text-[#F2EDE6]">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-[#A19D98] text-sm mb-2">Question *</label>
                <Input
                  name="question"
                  value={formData.question}
                  onChange={handleInputChange}
                  required
                  placeholder="How do I sign up for a rally?"
                  data-testid="faq-question-input"
                  className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                />
              </div>

              <div>
                <label className="block text-[#A19D98] text-sm mb-2">Answer *</label>
                <Textarea
                  name="answer"
                  value={formData.answer}
                  onChange={handleInputChange}
                  required
                  rows={6}
                  placeholder="Detailed answer to the question..."
                  data-testid="faq-answer-input"
                  className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                />
              </div>

              <div>
                <label className="block text-[#A19D98] text-sm mb-2">Order</label>
                <Input
                  type="number"
                  name="order"
                  value={formData.order}
                  onChange={handleInputChange}
                  data-testid="faq-order-input"
                  className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6] w-24"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">
                  Cancel
                </button>
                <button type="submit" data-testid="faq-submit-btn" className="btn-primary flex-1">
                  {editing ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFAQs;
